import { useState } from "react";
import Icon from "../../../components/base/Icon";
import { toastError } from "../../../components/base/Toast";
import { useHub } from "../../../core/Global";
import SignupActions from "./SignupActions";
import "./SignupBuilder.css";
import "./SignupJobTypes.css";


interface SignupJobTypesProps {
    /** Called once every job type has been created. */
    onDone: () => void
    /** Move on without adding anything — nothing in the setup wizard is compulsory. */
    onSkip: () => void
    /** Leave the whole setup and open the console. */
    onSkipSetup: () => void
}

interface DraftGroup {
    name: string
    types: string[]
    /** The name box is open and waiting for a first type. */
    adding?: boolean
}

// A starting point rather than a decision: most of the businesses on Rinvox do some of this,
// and picking from a list is far quicker than typing eight names into an empty page. Everything
// here is editable and removable before it is saved.
const SUGGESTIONS: DraftGroup[] = [
    { name: "Locksmith", types: ["Car Lockout", "House Lockout", "Key Duplication", "Lock Rekey"] },
    { name: "Garage Door", types: ["Spring Repair", "Opener Repair", "New Installation"] },
    { name: "Appliance Repair", types: ["Refrigerator", "Washer & Dryer", "Oven"] },
];


// First step of setting the account up: the kinds of work this business does, grouped.
//
// Job types are the base data the whole CRM hangs off — a job cannot be booked, priced, routed
// to a technician or reported on without one — which is why this comes before extensions,
// queues and phone numbers.
export default function SignupJobTypes(props: SignupJobTypesProps) {

    const hub = useHub();

    const [groups, groupsSet] = useState<DraftGroup[]>([]);
    const [groupName, groupNameSet] = useState<string>("");
    const [typeDraft, typeDraftSet] = useState<{ [group: string]: string }>({});
    const [working, workingSet] = useState<boolean>(false);

    const addGroup = (name: string) => {
        const clean = name.trim();
        if (clean.length < 2) return;
        if (groups.some(g => g.name.toLowerCase() === clean.toLowerCase())) {
            toastError("You already have a group with that name.");
            return;
        }
        groupsSet([...groups, { name: clean, types: [], adding: true }]);
        groupNameSet("");
    };

    const removeGroup = (name: string) => groupsSet(groups.filter(g => g.name !== name));

    const addType = (group: string) => {
        const clean = (typeDraft[group] ?? "").trim();
        if (clean.length < 2) return;

        groupsSet(groups.map(g => {
            if (g.name !== group) return g;
            if (g.types.some(t => t.toLowerCase() === clean.toLowerCase())) return g;
            return { ...g, types: [...g.types, clean] };
        }));

        typeDraftSet({ ...typeDraft, [group]: "" });
    };

    const removeType = (group: string, type: string) => {
        groupsSet(groups.map(g => g.name === group ? { ...g, types: g.types.filter(t => t !== type) } : g));
    };

    // Not named useSomething: the lint rules read any use-prefixed function as a React Hook and
    // refuse to see it called from a click handler.
    const applySuggestion = (s: DraftGroup) => {
        if (groups.some(g => g.name.toLowerCase() === s.name.toLowerCase())) return;
        groupsSet([...groups, { name: s.name, types: [...s.types] }]);
    };

    const totalTypes = groups.reduce((n, g) => n + g.types.length, 0);

    // What is left to offer. Taking the first suggestion used to take the whole strip away with
    // it, so a business that does two of these had to type the second one out by hand. One that
    // has been added drops off the strip -- that is the acknowledgement that it landed -- and
    // the rest stay where they were.
    const available = SUGGESTIONS.filter(s =>
        !groups.some(g => g.name.toLowerCase() === s.name.toLowerCase()));

    // The groups are created first and the types are then posted with their group's ID.
    //
    // Posting the group's NAME with each type and letting the endpoint make the group when it
    // does not recognise the name reads well, but every type went out at once — so none of them
    // saw the group any of the others had just made, and a group of four types came back as four
    // groups of one. Names cannot be relied on to deduplicate concurrent requests.
    const save = () => {
        const wanted = groups.filter(g => g.types.length > 0);

        // Nothing added means nothing to create, so Continue is simply the way past this step.
        if (wanted.length === 0) {
            props.onSkip();
            return;
        }

        workingSet(true);

        const fail = (message?: string) => {
            workingSet(false);
            toastError(message ?? "The job types could not be created. Please try again.");
        };

        // Any group the account already has is reused rather than made a second time.
        hub.Get("/base/jobtypegroup/all", existing => {
            if (!existing.success) return fail(existing.message);

            const byName: { [name: string]: string } = {};
            (existing.data ?? []).forEach((g: any) => {
                if (g?.name && g?.id) byName[g.name.toLowerCase()] = g.id;
            });

            // One at a time, so two groups of the same name can never be made at once. There are
            // only ever a handful of them.
            const makeGroups = (i: number, done: () => void): void => {
                if (i >= wanted.length) return done();

                const group = wanted[i];
                if (byName[group.name.toLowerCase()]) return makeGroups(i + 1, done);

                hub.Post("/base/jobtypegroup", { name: group.name }, res => {
                    if (!res.success || !res.data?.id) return fail(res.message);
                    byName[group.name.toLowerCase()] = res.data.id;
                    makeGroups(i + 1, done);
                });
            };

            makeGroups(0, () => {
                // Safe to send together now: each one carries the id of a group that exists.
                const pending = wanted.flatMap(g => g.types.map(name => ({ name, groupId: byName[g.name.toLowerCase()] })));

                var left = pending.length;
                var failed = false;

                pending.forEach(item => {
                    hub.Post("/base/jobtype", { name: item.name, group: item.groupId }, res => {
                        if (!res.success) failed = true;
                        left--;
                        if (left > 0) return;

                        workingSet(false);
                        if (failed) toastError("Some job types could not be created. Please check them and try again.");
                        else props.onDone();
                    });
                });
            });
        });
    };

    return (
        <div className="signup-jobtypes">

            {
                available.length > 0 &&
                <div className="jobtypes-suggestions">
                    <div className="text-gray-500 fs-7 mb-3">
                        {
                            groups.length === 0
                                ? "Start from one of these, or add your own below."
                                : "Add another, or add your own below."
                        }
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {
                            available.map(s =>
                                <button type="button" key={s.name} className="jobtypes-suggestion"
                                    onClick={() => applySuggestion(s)}>
                                    <Icon icon="plus" type="solid" size={11} className="me-2" />
                                    {s.name}
                                    <span className="text-gray-500 ms-2">{s.types.length}</span>
                                </button>
                            )
                        }
                    </div>
                </div>
            }

            {
                groups.map(group =>
                    <div key={group.name} className="signup-box">

                        <div className="signup-box-head">
                            <Icon icon="folder" type="solid" size={14} className="text-gray-500 me-2" />
                            <span className="signup-box-name">{group.name}</span>
                            <span className="signup-box-count">
                                {group.types.length} {group.types.length === 1 ? "type" : "types"}
                            </span>
                            <button type="button" className="signup-box-remove ms-auto"
                                title="Remove this group"
                                onClick={() => removeGroup(group.name)}>
                                <Icon icon="xmark" type="solid" size={13} />
                            </button>
                        </div>

                        {/* The box first, then what has been added under it: a new type lands
                            directly below the field it was typed into, where the eye already is. */}
                        <div className="signup-add">
                            <input className="form-control form-control-sm form-inputs"
                                placeholder={`Add a job type to ${group.name}`}
                                value={typeDraft[group.name] ?? ""}
                                autoFocus={group.adding}
                                onChange={e => typeDraftSet({ ...typeDraft, [group.name]: e.target.value })}
                                onKeyDown={e => {
                                    if (e.key !== "Enter") return;
                                    // The step sits inside a form; Enter here adds a type rather
                                    // than submitting it.
                                    e.preventDefault();
                                    addType(group.name);
                                }} />
                            <button type="button" className="btn btn-sm btn-icon btn-light-primary"
                                onClick={() => addType(group.name)}>
                                <Icon icon="plus" type="solid" size={12} />
                            </button>
                        </div>

                        {
                            group.types.length > 0 &&
                            <div className="signup-chips">
                                {
                                    group.types.map(type =>
                                        <span key={type} className="signup-chip">
                                            {type}
                                            <button type="button" onClick={() => removeType(group.name, type)}>
                                                <Icon icon="xmark" type="solid" size={10} />
                                            </button>
                                        </span>
                                    )
                                }
                            </div>
                        }

                    </div>
                )
            }

            <div className="signup-add signup-add-group">
                <input className="form-control form-inputs"
                    placeholder="Add a group — Locksmith, Garage Door, Plumbing…"
                    value={groupName}
                    onChange={e => groupNameSet(e.target.value)}
                    onKeyDown={e => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        addGroup(groupName);
                    }} />
                <button type="button" className="btn btn-light-primary" onClick={() => addGroup(groupName)}>
                    <Icon icon="plus" type="solid" size={13} className="me-2" />
                    Add Group
                </button>
            </div>

            <SignupActions>
                <button type="button" className="btn btn-light"
                    onClick={props.onSkipSetup} disabled={working}>
                    Skip setup &amp; go to console
                </button>

                <div className="text-gray-500 fs-8 ms-auto d-none d-md-block">
                    {
                        totalTypes > 0 &&
                        `${totalTypes} job ${totalTypes === 1 ? "type" : "types"} in ${groups.length} ${groups.length === 1 ? "group" : "groups"}`
                    }
                </div>

                {/* Never blocked: with nothing added it simply moves on, the same as every other
                    step of the setup. */}
                <button type="button" className={"btn btn-lg btn-primary min-w-200px" + (totalTypes > 0 ? "" : " ms-auto")}
                    disabled={working} onClick={save}>
                    {
                        working ?
                            <>
                                <span className="indicator-label me-2">Please Wait</span>
                                <Icon icon="spinner-third" spin />
                            </>
                            :
                            <>
                                <span className="indicator-label me-2">Continue</span>
                                <Icon icon="chevron-right" />
                            </>
                    }
                </button>
            </SignupActions>

        </div>
    );
}
